import { useEffect, useRef } from "react";
import type { DesignSpec } from "@/types/designSpec";
import {
  clearQuoteResumeAfterAuth,
  hasPendingQuoteResumeAfterAuth,
  hasQuoteResumeAfterAuth,
  QUOTE_RESUME_ID_PARAM,
  resumeQuoteAfterAuth,
} from "@/utils/quoteResumePersistence";

const RESTORATION_MISMATCH_GRACE_MS = 15_000;

/**
 * Resumes the one-shot quote intent carried through an existing-account login.
 * A restored image path is the readiness signal because auth redirect restores
 * the form, ticket, image, and chat in the same store update. Removing the URL
 * marker before invoking the action makes reloads and Strict Mode harmless.
 */
export function usePendingQuoteResume({
  authLoading,
  hasAccount,
  frontImagePath,
  resumeKey,
  onResume,
  onIntentUnavailable,
}: {
  authLoading: boolean;
  hasAccount: boolean;
  frontImagePath: string | null;
  resumeKey: string | null;
  onResume: (
    designSpecOverride?: DesignSpec,
    options?: { quoteScope?: "front_only" }
  ) => boolean | Promise<boolean>;
  onIntentUnavailable?: () => void;
}) {
  const resumeStartedRef = useRef(false);

  useEffect(() => {
    if (
      resumeStartedRef.current ||
      authLoading ||
      !hasAccount ||
      typeof window === "undefined"
    ) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get("quote_intent") !== "1") return;
    const intentId = currentUrl.searchParams.get(QUOTE_RESUME_ID_PARAM);

    const clearVisibleIntent = () => {
      const latestUrl = new URL(window.location.href);
      latestUrl.searchParams.delete("quote_intent");
      latestUrl.searchParams.delete(QUOTE_RESUME_ID_PARAM);
      window.history.replaceState(
        {},
        "",
        latestUrl.pathname + latestUrl.search + latestUrl.hash
      );
    };

    // Local save-point restoration is normally synchronous. Give slow auth
    // hydration a small bounded window, then expire the capability so a dress
    // generated much later cannot be submitted as the old quote intent.
    if (!intentId) {
      resumeStartedRef.current = true;
      clearVisibleIntent();
      onIntentUnavailable?.();
      return;
    }

    if (!frontImagePath || !resumeKey) {
      if (!hasPendingQuoteResumeAfterAuth(intentId)) {
        resumeStartedRef.current = true;
        clearQuoteResumeAfterAuth(intentId);
        clearVisibleIntent();
        onIntentUnavailable?.();
        return;
      }

      const missingRestoreTimer = window.setTimeout(() => {
        resumeStartedRef.current = true;
        clearQuoteResumeAfterAuth(intentId);
        clearVisibleIntent();
        onIntentUnavailable?.();
      }, RESTORATION_MISMATCH_GRACE_MS);
      return () => window.clearTimeout(missingRestoreTimer);
    }

    // Existing-account sign-in can hydrate that account's previous /create
    // state for one render before the anonymous save point is adopted. A
    // browser-local intent for a different dress therefore means "restoration
    // is still settling", not "discard the intent". Never submit the transient
    // dress, and bound the wait so a failed restoration cannot become a future
    // surprise submission.
    if (!hasQuoteResumeAfterAuth(intentId, resumeKey)) {
      if (!hasPendingQuoteResumeAfterAuth(intentId)) {
        resumeStartedRef.current = true;
        clearQuoteResumeAfterAuth(intentId);
        clearVisibleIntent();
        onIntentUnavailable?.();
        return;
      }

      const mismatchTimer = window.setTimeout(() => {
        resumeStartedRef.current = true;
        clearQuoteResumeAfterAuth(intentId);
        clearVisibleIntent();
        onIntentUnavailable?.();
      }, RESTORATION_MISMATCH_GRACE_MS);
      return () => window.clearTimeout(mismatchTimer);
    }

    resumeStartedRef.current = true;
    void (async () => {
      try {
        const outcome = await resumeQuoteAfterAuth(
          intentId,
          resumeKey,
          onResume
        );
        if (outcome === "retry") {
          clearQuoteResumeAfterAuth(intentId);
          clearVisibleIntent();
          onIntentUnavailable?.();
          return;
        }
        // An unavailable lock or an already-consumed capability must not keep
        // an auto-submit marker alive in this tab. The shared record remains
        // untouched unless the lock owner completed the exact action.
        clearVisibleIntent();
        if (outcome === "unavailable") onIntentUnavailable?.();
      } catch {
        clearQuoteResumeAfterAuth(intentId);
        clearVisibleIntent();
        onIntentUnavailable?.();
      }
    })();
  }, [
    authLoading,
    hasAccount,
    frontImagePath,
    resumeKey,
    onResume,
    onIntentUnavailable,
  ]);
}
